import React, { useState, useEffect } from 'react';
import {
  DocumentChartBarIcon,
  CalendarIcon,
  BuildingOfficeIcon,
  ArrowDownTrayIcon,
  ClockIcon,
  ChartBarIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  UserGroupIcon,
  MapPinIcon,
  LightBulbIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { useQuery, useMutation } from '@tanstack/react-query';
import apiService from '../services/apiService';
import PageHeader from '../components/ui/PageHeader';
import Button from '../components/ui/Button';
import EmptyState from '../components/ui/EmptyState';

interface CouncilPackOptions {
  timeframe: 'monthly' | 'quarterly' | 'yearly';
  department: string;
  includeCharts: boolean;
  includeRawData: boolean;
  format: 'pdf' | 'excel' | 'powerpoint';
}

interface CouncilPackData {
  // AI-generated plain-language summary of the figures below — prose only,
  // never a source of new numbers. Null when the feature is unavailable.
  narrativeSummary?: string | null;
  executiveSummary: {
    totalReports: number;
    resolvedReports: number;
    pendingReports: number;
    averageResolutionTime: number;
    citizenSatisfaction: number;
    departmentPerformance: Array<{
      name: string;
      reports: number;
      resolved: number;
      avgResolutionTime: number;
      satisfaction: number;
    }>;
  };
  kpis: {
    firstResponseTime: number;
    resolutionRate: number;
    citizenSatisfaction: number;
    costPerReport: number;
    efficiencyScore: number;
  };
  trends: {
    monthly: Array<{
      month: string;
      reports: number;
      resolved: number;
      avgResolutionTime: number;
    }>;
    quarterly: Array<{
      quarter: string;
      reports: number;
      resolved: number;
      avgResolutionTime: number;
    }>;
  };
  hotspots: {
    criticalAreas: Array<{
      location: string;
      reportCount: number;
      priority: string;
      lastReport: string;
    }>;
    recurrentIssues: Array<{
      issue: string;
      frequency: number;
      impact: string;
    }>;
  };
  recommendations: Array<{
    category: string;
    action: string;
    impact: string;
    priority: 'high' | 'medium' | 'low';
    estimatedCost: number;
  }>;
}

const CouncilPacks: React.FC = () => {
  const [options, setOptions] = useState<CouncilPackOptions>({
    timeframe: 'monthly',
    department: 'all',
    includeCharts: true,
    includeRawData: true,
    format: 'pdf'
  });

  const [councilPackData, setCouncilPackData] = useState<CouncilPackData | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  // Fetch departments
  const { data: departmentsData } = useQuery({
    queryKey: ['departments'],
    queryFn: async () => {
      const response = await apiService.get('/departments');
      return response.data || [];
    }
  });

  const departments = departmentsData || [];

  // Generate council pack
  const generateMutation = useMutation({
    mutationFn: async (options: CouncilPackOptions) => {
      const params = new URLSearchParams({
        timeframe: options.timeframe,
        department: options.department,
      });
      const response = await apiService.get(`/council-packs/generate?${params}`);
      return response.data;
    },
    onSuccess: (data) => {
      setCouncilPackData(data);
    },
    onError: (error) => {
      console.error('Error generating council pack:', error);
      alert('Failed to generate council pack. Please try again.');
    }
  });

  // Export council pack
  const exportMutation = useMutation({
    mutationFn: async (options: CouncilPackOptions) => {
      const params = new URLSearchParams({
        timeframe: options.timeframe,
        department: options.department,
        format: options.format,
        includeCharts: options.includeCharts.toString(),
        includeRawData: options.includeRawData.toString(),
      });
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1'}/council-packs/export?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      if (!response.ok) {
        throw new Error('Export failed');
      }
      return response.blob();
    },
    onSuccess: (blob, options) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const extension = options.format === 'powerpoint' ? 'pptx' : options.format;
      a.download = `council_pack_${options.department}_${options.timeframe}_${new Date().toISOString().split('T')[0]}.${extension}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    },
    onError: (error) => {
      console.error('Error exporting council pack:', error);
      alert('Failed to export council pack. Please try again.');
    }
  });

  const handleGenerate = async () => {
    setIsGenerating(true);
    try {
      await generateMutation.mutateAsync(options);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleExport = async () => {
    if (!councilPackData) {
      alert('Please generate a report first before exporting.');
      return;
    }
    await exportMutation.mutateAsync(options);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'text-red-600 bg-red-100 border-red-300';
      case 'medium': return 'text-yellow-600 bg-yellow-100 border-yellow-300';
      case 'low': return 'text-green-600 bg-green-100 border-green-300';
      default: return 'text-gray-600 dark:text-gray-400 bg-gray-100 border-gray-300';
    }
  };

  const formatTime = (hours: number) => {
    if (hours < 1) return `${Math.round(hours * 60)} minutes`;
    if (hours < 24) return `${hours.toFixed(1)} hours`;
    return `${(hours / 24).toFixed(1)} days`;
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div>
      <PageHeader
        title="Council Packs — Executive Reporting"
        subtitle="Generate comprehensive executive reports for strategic decision-making and stakeholder communication."
      />

      {/* Configuration Panel */}
      <div className="rounded-sm border border-gray-300 bg-white p-6 dark:border-gray-700 dark:bg-gray-800 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Report Configuration</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {/* Timeframe Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <CalendarIcon className="h-4 w-4 inline mr-2" />
              Timeframe
            </label>
            <select
              value={options.timeframe}
              onChange={(e) => setOptions({ ...options, timeframe: e.target.value as any })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="monthly">Monthly Report</option>
              <option value="quarterly">Quarterly Report</option>
              <option value="yearly">Annual Report</option>
            </select>
          </div>

          {/* Department Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <BuildingOfficeIcon className="h-4 w-4 inline mr-2" />
              Department
            </label>
            <select
              value={options.department}
              onChange={(e) => setOptions({ ...options, department: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="all">All Departments</option>
              {departments.map((dept: any) => (
                <option key={dept.id} value={dept.name}>
                  {dept.name}
                </option>
              ))}
            </select>
          </div>

          {/* Format Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <ArrowDownTrayIcon className="h-4 w-4 inline mr-2" />
              Export Format
            </label>
            <select
              value={options.format}
              onChange={(e) => setOptions({ ...options, format: e.target.value as any })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="pdf">PDF</option>
              <option value="excel">Excel</option>
              <option value="powerpoint">PowerPoint</option>
            </select>
          </div>

          {/* Options */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <ChartBarIcon className="h-4 w-4 inline mr-2" />
              Include
            </label>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={options.includeCharts}
                  onChange={(e) => setOptions({ ...options, includeCharts: e.target.checked })}
                  className="mr-2"
                />
                Charts & Visualizations
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={options.includeRawData}
                  onChange={(e) => setOptions({ ...options, includeRawData: e.target.checked })}
                  className="mr-2"
                />
                Raw Data
              </label>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4 mt-6">
          <Button
            variant="primary"
            icon={isGenerating || generateMutation.isPending ? ClockIcon : DocumentChartBarIcon}
            disabled={isGenerating || generateMutation.isPending}
            onClick={handleGenerate}
          >
            {isGenerating || generateMutation.isPending ? 'Generating…' : 'Generate Report'}
          </Button>

          <Button
            className="border-emerald-700 bg-emerald-700 text-white hover:bg-emerald-800"
            icon={exportMutation.isPending ? ClockIcon : ArrowDownTrayIcon}
            disabled={!councilPackData || exportMutation.isPending}
            onClick={handleExport}
          >
            {exportMutation.isPending ? 'Exporting…' : 'Export'}
          </Button>
        </div>
      </div>

      {/* Quick Templates */}
      <div className="rounded-sm border border-gray-300 bg-white p-6 dark:border-gray-700 dark:bg-gray-800 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Quick Templates</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => {
              setOptions({ ...options, timeframe: 'monthly', department: 'all' });
              setCouncilPackData(null);
            }}
            className="p-4 border border-gray-200 rounded-lg hover:border-primary-400 hover:bg-primary-50 text-left transition-colors"
          >
            <h3 className="font-medium text-gray-900 dark:text-gray-100">Monthly Executive Summary</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Comprehensive monthly overview for board meetings</p>
          </button>

          <button
            onClick={() => {
              setOptions({ ...options, timeframe: 'quarterly', department: 'all' });
              setCouncilPackData(null);
            }}
            className="p-4 border border-gray-200 rounded-lg hover:border-primary-400 hover:bg-primary-50 text-left transition-colors"
          >
            <h3 className="font-medium text-gray-900 dark:text-gray-100">Quarterly Performance Review</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Detailed quarterly analysis with trends</p>
          </button>

          <button
            onClick={() => {
              setOptions({ ...options, timeframe: 'yearly', department: 'all' });
              setCouncilPackData(null);
            }}
            className="p-4 border border-gray-200 rounded-lg hover:border-primary-400 hover:bg-primary-50 text-left transition-colors"
          >
            <h3 className="font-medium text-gray-900 dark:text-gray-100">Annual Strategic Report</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Comprehensive yearly strategic overview</p>
          </button>
        </div>
      </div>

      {/* Generated Report Display */}
      {councilPackData && (
        <div className="space-y-6">
          {/* AI narrative summary — plain-language read of the figures below,
              generated only from data already computed server-side. Absent
              entirely (not shown as an error) when the feature is unavailable. */}
          {councilPackData.narrativeSummary && (
            <div className="rounded-sm border border-accent-500 bg-accent-50 p-5 dark:border-accent-500 dark:bg-gray-800">
              <h2 className="text-sm font-semibold text-primary-900 dark:text-gray-100 mb-2 flex items-center gap-2">
                <SparklesIcon className="h-5 w-5 text-accent-600" />
                Summary
              </h2>
              <p className="text-sm leading-relaxed text-gray-800 dark:text-gray-200">
                {councilPackData.narrativeSummary}
              </p>
            </div>
          )}

          {/* Executive Summary */}
          <div className="rounded-sm border border-gray-300 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6 flex items-center gap-2">
              <ChartBarIcon className="h-6 w-6 text-blue-600" />
              Executive Summary
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <div className="text-sm text-blue-600 font-medium">Total Reports</div>
                <div className="text-3xl font-bold text-blue-900 mt-1">{councilPackData.executiveSummary.totalReports}</div>
              </div>
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <div className="text-sm text-green-600 font-medium">Resolved</div>
                <div className="text-3xl font-bold text-green-900 mt-1">{councilPackData.executiveSummary.resolvedReports}</div>
                <div className="text-xs text-green-700 mt-1">
                  {councilPackData.executiveSummary.totalReports > 0 
                    ? Math.round((councilPackData.executiveSummary.resolvedReports / councilPackData.executiveSummary.totalReports) * 100)
                    : 0}%
                </div>
              </div>
              <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
                <div className="text-sm text-yellow-600 font-medium">Pending</div>
                <div className="text-3xl font-bold text-yellow-900 mt-1">{councilPackData.executiveSummary.pendingReports}</div>
              </div>
              <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                <div className="text-sm text-purple-600 font-medium">Avg Resolution Time</div>
                <div className="text-2xl font-bold text-purple-900 mt-1">
                  {formatTime(councilPackData.executiveSummary.averageResolutionTime)}
                </div>
              </div>
            </div>

            {/* Department Performance */}
            {councilPackData.executiveSummary.departmentPerformance.length > 0 && (
              <div className="mt-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Department Performance</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-900/50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Department</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Total Reports</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Resolved</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Resolution Rate</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Avg Resolution Time</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200 dark:divide-gray-700">
                      {councilPackData.executiveSummary.departmentPerformance.map((dept, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">{dept.name}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{dept.reports}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{dept.resolved}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                            {dept.reports > 0 ? Math.round((dept.resolved / dept.reports) * 100) : 0}%
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{formatTime(dept.avgResolutionTime)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* KPIs */}
          <div className="rounded-sm border border-gray-300 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6 flex items-center gap-2">
              <ArrowTrendingUpIcon className="h-6 w-6 text-green-600" />
              Key Performance Indicators
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="border rounded-lg p-4">
                <div className="text-sm text-gray-600 dark:text-gray-400 font-medium">First Response Time</div>
                <div className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">
                  {councilPackData.kpis.firstResponseTime.toFixed(1)} min
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Target: 30 minutes</div>
              </div>
              <div className="border rounded-lg p-4">
                <div className="text-sm text-gray-600 dark:text-gray-400 font-medium">Resolution Rate</div>
                <div className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">
                  {councilPackData.kpis.resolutionRate.toFixed(1)}%
                </div>
              </div>
              <div className="border rounded-lg p-4">
                <div className="text-sm text-gray-600 dark:text-gray-400 font-medium">Citizen Satisfaction</div>
                <div className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-1">
                  {councilPackData.kpis.citizenSatisfaction > 0 ? councilPackData.kpis.citizenSatisfaction.toFixed(1) : 'N/A'}
                </div>
              </div>
            </div>
          </div>

          {/* Hotspots */}
          {(councilPackData.hotspots.criticalAreas.length > 0 || councilPackData.hotspots.recurrentIssues.length > 0) && (
            <div className="rounded-sm border border-gray-300 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6 flex items-center gap-2">
                <MapPinIcon className="h-6 w-6 text-red-600" />
                Hotspots & Critical Areas
              </h2>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {councilPackData.hotspots.criticalAreas.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Critical Areas</h3>
                    <div className="space-y-3">
                      {councilPackData.hotspots.criticalAreas.map((area, idx) => (
                        <div key={idx} className="border border-red-200 rounded-lg p-4 bg-red-50">
                          <div className="flex items-center justify-between">
                            <div className="font-medium text-gray-900 dark:text-gray-100">{area.location}</div>
                            <span className="px-2 py-1 text-xs font-semibold bg-red-600 text-white rounded">
                              {area.reportCount} reports
                            </span>
                          </div>
                          <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            Last report: {formatDate(area.lastReport)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {councilPackData.hotspots.recurrentIssues.length > 0 && (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Recurrent Issues</h3>
                    <div className="space-y-3">
                      {councilPackData.hotspots.recurrentIssues.map((issue, idx) => (
                        <div key={idx} className="border rounded-lg p-4">
                          <div className="flex items-center justify-between">
                            <div className="font-medium text-gray-900 dark:text-gray-100">{issue.issue}</div>
                            <span className={`px-2 py-1 text-xs font-semibold rounded ${
                              issue.impact === 'high' ? 'bg-red-100 text-red-800' :
                              issue.impact === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                              'bg-green-100 text-green-800'
                            }`}>
                              {issue.impact}
                            </span>
                          </div>
                          <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            Frequency: {issue.frequency} occurrences
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Recommendations */}
          {councilPackData.recommendations.length > 0 && (
            <div className="rounded-sm border border-gray-300 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6 flex items-center gap-2">
                <LightBulbIcon className="h-6 w-6 text-yellow-600" />
                Recommendations
              </h2>
              
              <div className="space-y-4">
                {councilPackData.recommendations.map((rec, idx) => (
                  <div key={idx} className={`border-l-4 rounded-lg p-4 ${getPriorityColor(rec.priority)}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-2 py-1 text-xs font-semibold rounded ${getPriorityColor(rec.priority)}`}>
                            {rec.priority.toUpperCase()}
                          </span>
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{rec.category}</span>
                        </div>
                        <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">{rec.action}</div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">{rec.impact}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                          R{rec.estimatedCost.toLocaleString()}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Est. Cost</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Empty State */}
      {!councilPackData && !isGenerating && (
        <div className="rounded-sm border border-gray-300 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
          <EmptyState icon={DocumentChartBarIcon} title="No report generated yet" description="Use the configuration above to create your first council pack." />
        </div>
      )}
    </div>
  );
};

export default CouncilPacks;
